import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
    getCatalogItems,
    getSelectedCatalogItem,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getCanEditCountOfQty,
    getDisableChangeSupplierAndDeleteItems,
} from 'entities/Order/model/selectors/ordersPermissions';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrder,
    getSelectedOrderIsPreSubmission,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import {
    cancelSourcingOperation,
    editOrderItemSourcingActions,
    getIsSourcingInProgress,
    getIsSourcingOverridden,
    getShowTotalQuantityError,
    getTotalQuantity,
    getTotalSelectedPrice,
    updateTotalQuantityWithSourcing,
} from 'features/EditOrderItemSourcing';
import { RemoveItemFromOrder } from 'features/RemoveItemFromOrder';
import { getRouteForbidden } from 'shared/const/router';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import {
    cancelDebouncedCallback,
    useDebouncedCallback,
} from 'shared/lib/hooks/useDebouncedCallback';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { useUnmountEffect } from 'shared/lib/hooks/useUnmountEffect';
import { isArray } from 'shared/lib/lodash/lodash';
import { shouldShowUOM } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { OrderItemQuantityPicker } from 'shared/ui/OrderItemQuantityPicker';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';
import { Tooltip } from 'shared/ui/Tooltip';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';
import { UnitOfMeasureDisplay } from 'shared/ui/UnitOfMeasureDisplay/UnitOfMeasureDisplay';
import { useOrderItemHooks } from 'widgets/OrderGroupedItems/hooks/OrderItemHooks';

import { totalQuantityErrorId } from './components/SaveBarError';

import type { TFunction } from 'i18next';

const customSourcingTooltipContent = (t: TFunction) => {
    return t(
        // eslint-disable-next-line max-len
        "Adjusting the total quantity will overwrite the changes made to individual supplier quantities, using the property's sourcing preferences.",
    );
};

type AvailabilityStatusText = {
    mainText: string;
    secondaryText: string | null;
};

function getAvailabilityStatusText(
    t: TFunction,
    isPreSubmission: boolean,
    totalAvailable: number,
    totalAvailableSuppliers: number,
    totalUnav: number,
    totalAvail: number,
    unavailableSuppliers: number,
    fulfilledSupplierCount: number,
): AvailabilityStatusText {
    if (isPreSubmission) {
        if (totalAvailable > 0) {
            return {
                mainText: `${formatNumber(totalAvailable)} ${t('In stock')}`,
                secondaryText: `${t('at')} ${totalAvailableSuppliers} ${t(
                    'suppliers',
                )}`,
            };
        }
        return {
            mainText: t('Not available'),
            secondaryText: null,
        };
    }

    // Post-submission status
    if (totalUnav > 0 && totalAvail > 0) {
        return {
            mainText: t('Partially available'),
            secondaryText: `${t('from')} ${fulfilledSupplierCount} ${t(
                'supplier',
                {
                    count: fulfilledSupplierCount,
                },
            )}`,
        };
    }

    if (totalUnav > 0) {
        return {
            mainText: t('Unavailable'),
            secondaryText: `${t('at')} ${unavailableSuppliers} ${t('supplier', {
                count: unavailableSuppliers,
            })}`,
        };
    }

    if (totalAvail > 0) {
        return {
            mainText: `${formatNumber(totalAvail)} ${t('ordered')}`,
            secondaryText: `${t('from')} ${fulfilledSupplierCount} ${t(
                'supplier',
                {
                    count: fulfilledSupplierCount,
                },
            )}`,
        };
    }

    return {
        mainText: '',
        secondaryText: null,
    };
}

export const OrderViewItemDetailedInfo = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const item = useSelector(getSelectedCatalogItem);
    const catalogItems = useSelector(getCatalogItems);
    const canSeePrices = useSelector(getUserCanSeePrices);
    const selectedOrder = useSelector(getSelectedOrder);
    const totalQuantity = useSelector(getTotalQuantity);
    const showTotalQuantityError = useSelector(getShowTotalQuantityError);
    const isSourcingInProgress = useSelector(getIsSourcingInProgress);
    const totalSelectedPrice = useSelector(getTotalSelectedPrice);
    const isSourcingOverridden = useSelector(getIsSourcingOverridden);
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
    const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);
    const canEditQty = useSelector(getCanEditCountOfQty);
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);
    const disableChangeSupplierAndDeleteItems = useSelector(
        getDisableChangeSupplierAndDeleteItems,
    );

    const { openLightbox } = useImageLightbox();

    const selectedItem = item as CatalogItem | undefined;
    const items = catalogItems as CatalogItem[] | undefined;
    const showSourcingOverridden = isPreSubmission && isSourcingOverridden;
    const showPicker = isPreSubmission && canEditQty;
    const showPrices = canSeePrices && !isMaintenanceTechnician;

    // Debounced callback for total quantity updates to prevent excessive API calls to the sourcing engine
    const debounceSourcingUpdate = useDebouncedCallback(
        useCallback(
            (quantity: number) => {
                dispatch(
                    updateTotalQuantityWithSourcing({
                        quantity,
                    }),
                );
            },
            [dispatch],
        ),
    );

    const handleQuantityChange = useCallback(
        (newQuantity: number) => {
            dispatch(
                editOrderItemSourcingActions.updateTotalQuantity({
                    quantity: newQuantity,
                }),
            );

            if (newQuantity <= 0) {
                cancelDebouncedCallback(debounceSourcingUpdate);
                dispatch(cancelSourcingOperation());
                return;
            }

            debounceSourcingUpdate(newQuantity);
        },
        [dispatch, debounceSourcingUpdate],
    );

    useInitialEffect(() => {
        if (!selectedItem && isArray(items)) {
            let foundItem = items.find((item) => item.id === params?.itemId);

            if (foundItem) {
                dispatch(catalogItemActions.setSelectedItem(foundItem));
            } else {
                navigate(getRouteForbidden());
            }
        }
    });

    useUnmountEffect(() => {
        dispatch(catalogItemActions.setSelectedItem(null));
    });

    const totalAvailable = useMemo(
        () =>
            selectedItem?.proposals?.reduce(
                (acc, proposal) => acc + proposal.availableQuantity,
                0,
            ) || 0,
        [selectedItem],
    );

    const totalAvailableSuppliers = useMemo(
        () =>
            selectedItem?.proposals?.reduce(
                (acc, proposal) =>
                    acc + (proposal.availableQuantity > 0 ? 1 : 0),
                0,
            ) || 0,
        [selectedItem],
    );

    const isOverQuantity = useMemo(
        () => totalQuantity > totalAvailable && isPreSubmission,
        [totalQuantity, totalAvailable, isPreSubmission],
    );

    const {
        totalQtyFulfilled,
        totalQtyUnavailable,
        totalPriceFulfilled,
        unavailableSuppliers,
        allSuppliersUnavailable,
        fulfilledSupplierCount,
    } = useOrderItemHooks(selectedItem || ({} as CatalogItem));

    const availabilityStatus = useMemo(
        () =>
            getAvailabilityStatusText(
                t,
                isPreSubmission,
                totalAvailable,
                totalAvailableSuppliers,
                totalQtyUnavailable,
                totalQtyFulfilled,
                unavailableSuppliers,
                fulfilledSupplierCount,
            ),
        [
            t,
            isPreSubmission,
            totalAvailable,
            totalAvailableSuppliers,
            totalQtyUnavailable,
            totalQtyFulfilled,
            unavailableSuppliers,
            fulfilledSupplierCount,
        ],
    );

    if (!selectedItem) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: '20px',
                }}
            >
                <Box
                    onClick={() => openLightbox(selectedItem.thumbnailUrl)}
                    sx={{
                        width: '124px',
                        height: '124px',
                        flexGrow: 0,
                        flexShrink: 0,
                        cursor: 'pointer',
                        '&:hover': {
                            div: {
                                borderColor: '#bababa',
                            },
                        },
                    }}
                >
                    <CatalogItemImage
                        src={selectedItem.thumbnailUrl}
                        height="100%"
                        width="100%"
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '12px',
                        flexGrow: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '20px',
                        }}
                    >
                        <Typography
                            component="h4"
                            variant="h1"
                            data-testclass="order-item-title"
                            sx={{
                                flexGrow: 1,
                            }}
                        >
                            {selectedItem.title}
                        </Typography>
                        {!disableChangeSupplierAndDeleteItems && (
                            <Box
                                sx={{
                                    flexGrow: 0,
                                    marginTop: '-4px',
                                }}
                            >
                                <RemoveItemFromOrder itemId={selectedItem.id} />
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            color: 'rgba(0, 0, 0, 0.85)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        <Typography variant="body1">
                            {t('SKU')}:&nbsp;
                            <strong>{selectedItem.sku}</strong>
                        </Typography>
                        {shouldShowUOM(selectedItem.uom) && (
                            <Typography
                                variant="body1"
                                data-testclass="order-item-uom"
                            >
                                <UnitOfMeasureDisplay
                                    unitCode={selectedItem.uom}
                                    quantity={selectedItem.qtyPerUom}
                                />
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            <TableContainer>
                <Table
                    sx={{
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            borderBottom: 'none',
                        },
                        '& .MuiTableBody-root .MuiTableCell-root': {
                            '&:first-of-type': {
                                borderTop: '1px solid',
                                borderBottom: '1px solid',
                                borderLeft: '1px solid',
                                borderColor: 'grey.500',
                                borderRadius: '4px 0px 0px 4px',
                            },
                            '&:last-of-type': {
                                borderTop: '1px solid',
                                borderBottom: '1px solid',
                                borderRight: '1px solid',
                                borderRadius: '0px 4px 4px 0px',
                                borderColor: 'grey.500',
                            },
                            '&': {
                                borderTop: '1px solid',
                                borderColor: 'grey.500',
                                paddingTop: '22px',
                                paddingBottom: '22px',
                            },
                        },
                        borderCollapse: 'separate',
                        borderSpacing: '0 14px',
                    }}
                    data-testid="order-view-item-detailed-info-table"
                >
                    <TableHead
                        sx={{
                            '& th': {
                                paddingTop: '0px',
                                paddingBottom: '0px',
                                lineHeight: '1.6rem',
                            },
                        }}
                    >
                        <TableRow>
                            <TableCell>
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    align="center"
                                >
                                    &nbsp;
                                </Typography>
                            </TableCell>
                            {!isPreSubmission && (
                                <TableCell
                                    sx={{
                                        width: '80px',
                                        paddingLeft: '0px',
                                        paddingRight: '15px',
                                    }}
                                    align="right"
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                    >
                                        {t('Unav')}
                                    </Typography>
                                </TableCell>
                            )}

                            {!isPreSubmission && (
                                <TableCell
                                    sx={{
                                        width: '80px',
                                        paddingLeft: '0px',
                                        paddingRight: '15px',
                                    }}
                                    align="right"
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                    >
                                        {t('Avail')}
                                    </Typography>
                                </TableCell>
                            )}

                            {isPreSubmission && (
                                <TableCell
                                    align={showPicker ? 'center' : 'right'}
                                    sx={{
                                        width: '102px',
                                        paddingLeft: '0px',
                                        paddingRight: showPrices
                                            ? '15px'
                                            : '20px',
                                    }}
                                >
                                    <DynamicTooltip
                                        content={customSourcingTooltipContent(
                                            t,
                                        )}
                                        disabled={!showSourcingOverridden}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                color: showSourcingOverridden
                                                    ? '#3F6EB7'
                                                    : 'inherit',
                                            }}
                                        >
                                            {showSourcingOverridden
                                                ? t('Custom total qty')
                                                : t('Total qty')}
                                        </Typography>
                                    </DynamicTooltip>
                                </TableCell>
                            )}

                            {showPrices && (
                                <TableCell
                                    align="right"
                                    sx={{
                                        width: '100px',
                                        paddingRight: '20px',
                                        paddingLeft: '0px',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                    >
                                        {t('Total')}
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow key={selectedItem.id}>
                            <TableCell data-testid="stock-availability-status">
                                {availabilityStatus.mainText && (
                                    <>
                                        <Typography
                                            component="span"
                                            variant="body1"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            {availabilityStatus.mainText}
                                        </Typography>
                                        {availabilityStatus.secondaryText && (
                                            <>
                                                {' '}
                                                <Typography
                                                    component="span"
                                                    variant="body1"
                                                    sx={{
                                                        color: 'rgba(0, 0, 0, 0.50)',
                                                    }}
                                                >
                                                    {
                                                        availabilityStatus.secondaryText
                                                    }
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </TableCell>
                            {!isPreSubmission && (
                                <TableCell
                                    sx={{
                                        width: '80px',
                                        paddingLeft: '0px',
                                        paddingRight: '15px',
                                        textAlign: 'right',
                                        color: '#F00',
                                        fontWeight: 600,
                                    }}
                                >
                                    {totalQtyUnavailable > 0
                                        ? totalQtyUnavailable
                                        : ''}
                                </TableCell>
                            )}
                            {!isPreSubmission && (
                                <TableCell
                                    sx={{
                                        width: '80px',
                                        paddingLeft: '0px',
                                        paddingRight: '15px',
                                        textAlign: 'right',
                                    }}
                                >
                                    {totalQtyFulfilled > 0
                                        ? totalQtyFulfilled
                                        : ''}
                                </TableCell>
                            )}
                            {isPreSubmission && (
                                <TableCell
                                    sx={{
                                        verticalAlign: 'top',
                                        width: '102px',
                                        position: 'relative',
                                        paddingLeft: '0px',
                                        paddingRight: showPrices
                                            ? '15px'
                                            : '20px',
                                    }}
                                    align="center"
                                >
                                    {isOverQuantity && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '-30px',
                                                transform: 'translateY(-50%)',
                                            }}
                                        >
                                            <Tooltip
                                                isSecondaryColor={true}
                                                color="error.main"
                                                sx={{ lineHeight: 0 }}
                                                content={t(
                                                    'Requested quantity for this item exceeds available stock.',
                                                )}
                                            ></Tooltip>
                                        </Box>
                                    )}
                                    {showPicker ? (
                                        <DynamicTooltip
                                            content={customSourcingTooltipContent(
                                                t,
                                            )}
                                            disabled={!showSourcingOverridden}
                                        >
                                            <OrderItemQuantityPicker
                                                value={totalQuantity}
                                                maxQuantity={totalAvailable}
                                                minQuantity={0}
                                                error={
                                                    showTotalQuantityError &&
                                                    totalQuantity <= 0
                                                }
                                                errorDescribedBy={
                                                    totalQuantityErrorId
                                                }
                                                onQuantityChange={
                                                    handleQuantityChange
                                                }
                                                showLoading={
                                                    isSourcingInProgress
                                                }
                                                disabled={
                                                    isSourcingInProgress ||
                                                    isOrderSubmissionLocked
                                                }
                                                showCustomQuantity={
                                                    showSourcingOverridden
                                                }
                                            />
                                        </DynamicTooltip>
                                    ) : (
                                        <Typography sx={{ textAlign: 'right' }}>
                                            {totalQuantity}
                                        </Typography>
                                    )}
                                </TableCell>
                            )}
                            {showPrices && (
                                <TableCell
                                    align="right"
                                    sx={{
                                        verticalAlign: 'top',
                                        width: '100px',
                                        paddingRight: '20px',
                                        paddingLeft: '0px',
                                    }}
                                >
                                    {isPreSubmission ? (
                                        totalQuantity > 0 && (
                                            <OrderPriceDisplay
                                                width="55px"
                                                containerHeight="32px"
                                                typography="subtitle2"
                                                containerProps={{
                                                    display: 'block',
                                                    lineHeight: '3.2rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    color: 'common.black',
                                                }}
                                                justifyContent="flex-end"
                                                testClass="order-item-price-total"
                                                price={totalSelectedPrice}
                                                orderHasLoadingPrices={
                                                    !selectedItem.isCheckFinished
                                                }
                                                order={selectedOrder}
                                            />
                                        )
                                    ) : (
                                        <OrderPriceDisplay
                                            width="55px"
                                            containerHeight="32px"
                                            typography="subtitle2"
                                            containerProps={{
                                                display: 'block',
                                                lineHeight: '3.2rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                color: 'common.black',
                                                opacity: allSuppliersUnavailable
                                                    ? 0.35
                                                    : 1,
                                            }}
                                            justifyContent="flex-end"
                                            testClass="order-item-price-total"
                                            price={
                                                allSuppliersUnavailable
                                                    ? 0
                                                    : totalPriceFulfilled
                                            }
                                            orderHasLoadingPrices={
                                                !selectedItem.isCheckFinished
                                            }
                                            order={selectedOrder}
                                            suppressEmptyPrice={
                                                !allSuppliersUnavailable
                                            }
                                            showZeroAsPrice={
                                                allSuppliersUnavailable
                                            }
                                        />
                                    )}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};
